import { useEffect } from 'react';
import { 
  registerBarcodeContext, 
  setActiveBarcodeContext, 
  BarcodeInputTargetType 
} from '../services/barcodeService';

export interface BarcodeFormField {
  id: string;
  type: BarcodeInputTargetType;
}

interface UseBarcodeRegistrationProps {
  formId: string;
  formFields: BarcodeFormField[];
  isActive: boolean;
  contextType?: BarcodeInputTargetType;
}

/**
 * Hook for standardized barcode scanner context registration
 * @param formId Unique identifier for the form
 * @param formFields Array of fields that can receive barcode input
 * @param isActive Whether the form is currently active/visible
 * @param contextType Type of context (defaults to FORM_FIELD)
 * @returns void
 */
export const useBarcodeRegistration = ({
  formId,
  formFields,
  isActive,
  contextType = BarcodeInputTargetType.FORM_FIELD
}: UseBarcodeRegistrationProps): void => {
  useEffect(() => {
    // Register form context with barcode service
    const mappedFormFields = formFields.map(field => ({
      id: field.id,
      formId,
      type: field.type
    }));
    
    const unregister = registerBarcodeContext({
      id: formId,
      type: contextType,
      formFields: mappedFormFields
    });
    
    // Set as active context when form is active
    if (isActive) {
      setActiveBarcodeContext(formId);
    }
    
    // Clean up on unmount
    return () => {
      unregister();
    };
  }, [formId, formFields, isActive, contextType]);
}; 